import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Award,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  Loader2,
  Upload,
  FileText,
  Image,
  Eye,
  Download,
  Trash2,
  ChevronRight,
  Camera,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
import { resolveFileUrl } from '../lib/fileUtils';

const getStatusBgClass = (color) => {
  switch (color) {
    case 'green': return 'bg-green-100 text-green-800 border-green-300';
    case 'yellow': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    case 'orange': return 'bg-orange-100 text-orange-800 border-orange-300';
    case 'red': return 'bg-red-100 text-red-800 border-red-300';
    default: return 'bg-gray-100 text-gray-800 border-gray-300';
  }
};

const StatusIcon = ({ status, size = 'w-5 h-5' }) => {
  switch (status) {
    case 'good': return <CheckCircle className={`${size} text-green-600`} />;
    case 'warning': return <Clock className={`${size} text-yellow-600`} />;
    case 'critical': return <AlertTriangle className={`${size} text-orange-600`} />;
    case 'expired': return <XCircle className={`${size} text-red-600`} />;
    case 'missing': return <XCircle className={`${size} text-red-600`} />;
    default: return null;
  }
};

export default function MyCertifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [certifications, setCertifications] = useState([]);
  const [employeeName, setEmployeeName] = useState('');
  
  // Dialog state
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showFilePreview, setShowFilePreview] = useState(false);
  const [selectedCert, setSelectedCert] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchMyCertifications();
  }, []);

  const fetchMyCertifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/certifications/my`);
      setCertifications(response.data.certifications);
      setEmployeeName(response.data.employee_name);
    } catch (error) {
      console.error('Failed to fetch certifications:', error);
      toast.error('Failed to load certifications');
    } finally {
      setLoading(false);
    }
  };

  const handleCertClick = (cert) => {
    setSelectedCert(cert);
    setShowDetailDialog(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedCert) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload an image or PDF');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be less than 10MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/api/certifications/employees/${user.id}/${selectedCert.certification_type_id}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      // Update local state
      setSelectedCert(prev => ({
        ...prev,
        files: [...(prev.files || []), response.data.file]
      }));
      
      // Refresh list
      fetchMyCertifications();
      toast.success('Document uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteFile = async (fileUrl) => {
    if (!selectedCert) return;
    
    try {
      await axios.delete(
        `${API}/api/certifications/employees/${user.id}/${selectedCert.certification_type_id}/file`,
        { data: { file_url: fileUrl } }
      );
      
      setSelectedCert(prev => ({
        ...prev,
        files: prev.files.filter(f => f.url !== fileUrl)
      }));
      
      fetchMyCertifications();
      toast.success('Document deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    try {
      return format(parseISO(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  // Group certifications by status
  const groupedCerts = {
    attention: certifications.filter(c => ['expired', 'missing', 'critical'].includes(c.status_info?.status)),
    warning: certifications.filter(c => c.status_info?.status === 'warning'),
    valid: certifications.filter(c => c.status_info?.status === 'good')
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-20" data-testid="my-certifications-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate(-1)}
          className="p-2"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Award className="w-6 h-6 text-amber-500" />
            My Certifications
          </h1>
          <p className="text-sm text-slate-500">{employeeName}</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-2">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-3 text-center">
            <span className="text-2xl font-bold text-red-700">{groupedCerts.attention.length}</span>
            <p className="text-xs text-red-600">Needs Attention</p>
          </CardContent>
        </Card>
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-3 text-center">
            <span className="text-2xl font-bold text-yellow-700">{groupedCerts.warning.length}</span>
            <p className="text-xs text-yellow-600">Expiring Soon</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-3 text-center">
            <span className="text-2xl font-bold text-green-700">{groupedCerts.valid.length}</span>
            <p className="text-xs text-green-600">Valid</p>
          </CardContent>
        </Card>
      </div>

      {/* Needs Attention Section */}
      {groupedCerts.attention.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-red-600 uppercase tracking-wide mb-2 flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" />
            Needs Attention
          </h2>
          <div className="space-y-2">
            {groupedCerts.attention.map(cert => (
              <Card 
                key={cert.certification_type_id} 
                className="border-red-200 cursor-pointer hover:bg-red-50 transition-colors"
                onClick={() => handleCertClick(cert)}
              >
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <StatusIcon status={cert.status_info?.status} />
                    <div>
                      <p className="font-medium text-slate-900">{cert.certification_name}</p>
                      <p className="text-xs text-slate-500">
                        {cert.date_expires ? `Expired: ${formatDate(cert.date_expires)}` : 'No data entered'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {cert.files?.length > 0 && (
                      <Badge variant="outline" className="text-xs">{cert.files.length} docs</Badge>
                    )}
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Expiring Soon Section */}
      {groupedCerts.warning.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-yellow-600 uppercase tracking-wide mb-2 flex items-center gap-1">
            <Clock className="w-4 h-4" />
            Expiring Soon
          </h2>
          <div className="space-y-2">
            {groupedCerts.warning.map(cert => (
              <Card 
                key={cert.certification_type_id} 
                className="border-yellow-200 cursor-pointer hover:bg-yellow-50 transition-colors"
                onClick={() => handleCertClick(cert)}
              >
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <StatusIcon status={cert.status_info?.status} />
                    <div>
                      <p className="font-medium text-slate-900">{cert.certification_name}</p>
                      <p className="text-xs text-slate-500">
                        Expires: {formatDate(cert.date_expires)} ({cert.status_info?.label})
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {cert.files?.length > 0 && (
                      <Badge variant="outline" className="text-xs">{cert.files.length} docs</Badge>
                    )}
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Valid Certifications Section */}
      {groupedCerts.valid.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-green-600 uppercase tracking-wide mb-2 flex items-center gap-1">
            <CheckCircle className="w-4 h-4" />
            Valid Certifications
          </h2>
          <div className="space-y-2">
            {groupedCerts.valid.map(cert => (
              <Card 
                key={cert.certification_type_id} 
                className="border-green-200 cursor-pointer hover:bg-green-50 transition-colors"
                onClick={() => handleCertClick(cert)}
              >
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <StatusIcon status={cert.status_info?.status} />
                    <div>
                      <p className="font-medium text-slate-900">{cert.certification_name}</p>
                      <p className="text-xs text-slate-500">
                        Expires: {formatDate(cert.date_expires)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {cert.files?.length > 0 && (
                      <Badge variant="outline" className="text-xs">{cert.files.length} docs</Badge>
                    )}
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Certification Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StatusIcon status={selectedCert?.status_info?.status} size="w-6 h-6" />
              {selectedCert?.certification_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Status Badge */}
            <div className="flex justify-center">
              <Badge className={`text-sm px-4 py-1 ${getStatusBgClass(selectedCert?.status_info?.color)}`}>
                {selectedCert?.status_info?.status === 'good' ? 'Valid' : 
                 selectedCert?.status_info?.status === 'warning' ? `Expiring in ${selectedCert?.status_info?.label}` :
                 selectedCert?.status_info?.status === 'critical' ? `Expiring in ${selectedCert?.status_info?.label}` :
                 selectedCert?.status_info?.status === 'expired' ? 'Expired' : 'No Data'}
              </Badge>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 uppercase">Completed</p>
                <p className="font-medium">{formatDate(selectedCert?.date_completed)}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 uppercase">Expires</p>
                <p className="font-medium">{formatDate(selectedCert?.date_expires)}</p>
              </div>
            </div>

            {/* Notes */}
            {selectedCert?.notes && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 uppercase mb-1">Notes</p>
                <p className="text-sm">{selectedCert.notes}</p>
              </div>
            )}

            {/* Documents Section */}
            <div>
              <p className="text-sm font-medium mb-2">Certification Documents</p>
              
              {/* Existing Files */}
              {selectedCert?.files?.length > 0 ? (
                <div className="space-y-2 mb-3">
                  {selectedCert.files.map((file, idx) => (
                    <div 
                      key={file.id || idx} 
                      className="flex items-center justify-between p-2 bg-slate-50 rounded border"
                    >
                      <div 
                        className="flex items-center gap-2 flex-1 cursor-pointer"
                        onClick={() => {
                          setPreviewFile(file);
                          setShowFilePreview(true);
                        }}
                      >
                        {file.content_type?.includes('pdf') ? (
                          <FileText className="w-5 h-5 text-red-500 flex-shrink-0" />
                        ) : (
                          <Image className="w-5 h-5 text-blue-500 flex-shrink-0" />
                        )}
                        <span className="text-sm truncate">{file.filename || 'Document'}</span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(resolveFileUrl(file), '_blank')}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500"
                          onClick={() => handleDeleteFile(file.url)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-3 bg-slate-50 rounded mb-3">
                  No documents uploaded yet
                </p>
              )}

              {/* Upload Button */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf"
                capture="environment"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button
                variant="outline"
                className="w-full"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4 mr-2" />
                )}
                {uploading ? 'Uploading...' : 'Upload Photo or Document'}
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowDetailDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* File Preview Dialog */}
      <Dialog open={showFilePreview} onOpenChange={setShowFilePreview}>
        <DialogContent className="max-w-lg max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{previewFile?.filename || 'Document'}</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center overflow-auto">
            {previewFile?.content_type?.includes('pdf') ? (
              <iframe
                src={resolveFileUrl(previewFile)}
                className="w-full h-[60vh] border rounded"
                title="PDF Preview"
              />
            ) : (
              <img
                src={resolveFileUrl(previewFile)}
                alt="Certificate"
                className="max-w-full max-h-[60vh] object-contain rounded"
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => window.open(resolveFileUrl(previewFile), '_blank')}>
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
            <Button onClick={() => setShowFilePreview(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
